import { queryClient } from '@/lib/api-client'
import { getAssetUrl } from '@/config/assetUrl'
import { mediaCacheKey } from '@/lib/media/mediaCacheKey'
import { sanitizeSvgPlanText } from '@/components/common/location/sanitizeSvgPlan.ts'
import { StageLocations, SingleLocation } from '@/routes/projectStages/issue/types/issue'

const SVG_QUERY_KEY_PREFIX = 'svgAsset'
/**
 * Bumped whenever what is stored under the key changes. v2 holds SANITISED plan text;
 * the unversioned key held the upload byte for byte and is never served again.
 */
const SVG_QUERY_KEY_VERSION = 'v2'

/**
 * The key a plan's sanitised text lives under — the one every `useQuery` over it must share.
 * It holds `mediaCacheKey(contentUrl)`, never the signed URL: the persisted cache must not keep
 * a signature, and a re-signed URL for the same plan has to find it offline. Unsigned URLs are
 * their own key, so no version bump.
 */
export function svgQueryKey(contentUrl: string) {
  return [SVG_QUERY_KEY_PREFIX, SVG_QUERY_KEY_VERSION, mediaCacheKey(contentUrl)] as const
}

/**
 * The pre-v2 key, restored from IndexedDB on older installs. Read to migrate, never served.
 * Those entries predate signing, so they sit under the unsigned URL — the canonical key.
 */
function legacyRawSvgQueryKey(contentUrl: string) {
  return [SVG_QUERY_KEY_PREFIX, mediaCacheKey(contentUrl)] as const
}

function trySanitize(svgText: string): string | null {
  try {
    return sanitizeSvgPlanText(svgText)
  } catch {
    return null
  }
}

/**
 * Fetches SVG text content with React Query persistence.
 * Checks the queryClient cache first (persisted to IndexedDB via idb-keyval),
 * then falls back to network fetch. Result is stored in the cache for offline access.
 *
 * The cache holds SANITISED text. This is the boundary: the copy persisted to IndexedDB
 * never carries the raw upload, and every reader — the map overlay, the pin preview, the
 * editor — gets text that has been through `sanitizeSvgPlanText` (a fixed point, so a
 * second pass at render time changes nothing). An entry persisted under the pre-v2 key
 * is raw: it is sanitised and re-keyed, or dropped when it is not a plan at all, and is
 * removed either way so the raw text is never handed out. A download that is not a plan
 * is not cached; the error reaches the caller the way a failed fetch does.
 */
export async function getSvgText(contentUrl: string, signal?: AbortSignal): Promise<string> {
  const queryKey = svgQueryKey(contentUrl)
  const cached = queryClient.getQueryData<string>(queryKey)
  if (cached) {
    return cached
  }

  const legacyKey = legacyRawSvgQueryKey(contentUrl)
  const legacyRaw = queryClient.getQueryData<string>(legacyKey)
  if (legacyRaw !== undefined) {
    queryClient.removeQueries({ queryKey: legacyKey, exact: true })
    const migrated = trySanitize(legacyRaw)
    if (migrated !== null) {
      queryClient.setQueryData(queryKey, migrated)
      return migrated
    }
  }

  const resolvedUrl = getAssetUrl(contentUrl)
  const response = await fetch(resolvedUrl, {
    credentials: 'include',
    signal,
  })

  if (!response.ok) {
    throw new Error(`Failed to fetch SVG: ${response.statusText}`)
  }

  const svgText = sanitizeSvgPlanText(await response.text())

  queryClient.setQueryData(queryKey, svgText)

  return svgText
}

/**
 * Recursively walks a location tree and collects unique SVG contentUrl values.
 */
export function collectSvgUrlsFromLocations(locations: StageLocations): string[] {
  const urls = new Set<string>()

  function walk(location: SingleLocation): void {
    if (location.map?.contentUrl) {
      urls.add(location.map.contentUrl)
    }
    if (location.children) {
      for (const child of location.children) {
        walk(child as SingleLocation)
      }
    }
  }

  for (const location of locations) {
    walk(location)
  }

  return [...urls]
}
